import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SCREENS } from '../lib/screenRegistry';
import {
  getOrCreateGuestSessionId,
  savePendingGuestTranscript,
  STRESS_TEST_FOCUS_AREAS,
  STRESS_TEST_VOICE_ID,
  type GuestTranscriptMessage,
  type StressTestFocusId,
} from '../lib/guestSession';
import {
  buildGuestStressTestResponse,
  countUserTurns,
  getAllStressTestScripts,
  stressTestScriptCacheKey,
} from '../lib/guestStressTestEngine';
import {
  cancelSpeechPlayback,
  prepareSpeechAudio,
  prepareSpeechAudioFromBlob,
  resetAudioPlaybackGate,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import { voiceApi } from '../services/api';
import './StressTestSandbox.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

/** Dr. André Smit clone — multilingual v2 + tuned voice_settings on the server. */
const STRESS_TEST_TTS_MODEL = 'eleven_multilingual_v2';

const WELCOME_SCRIPT =
  'Welcome to the 10-Minute Business Strategy Stress-Test. Pick a focus area to begin — I\'ll help you pressure-test the assumptions underneath how the business actually runs.';

interface ChatMessage extends GuestTranscriptMessage {
  focusArea?: string;
}

function estimateSpeechDurationMs(script: string) {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(3000, (words / 150) * 60 * 1000);
}

export default function StressTestSandbox() {
  const navigate = useNavigate();
  const streamRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const speechRef = useRef<HTMLAudioElement | null>(null);
  const syncRafRef = useRef<number | null>(null);
  const syncStartRef = useRef(0);
  const syncDurationRef = useRef(0);
  const audioCacheRef = useRef<Map<string, Blob>>(new Map());
  const prefetchInFlightRef = useRef<Map<string, Promise<Blob | null>>>(new Map());
  const micActiveRef = useRef(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [focusArea, setFocusArea] = useState<StressTestFocusId | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const [typedContent, setTypedContent] = useState('');
  const [voiceReady, setVoiceReady] = useState(false);

  const prefetchScript = useCallback(async (focus: StressTestFocusId, turn: number, content: string) => {
    const key = stressTestScriptCacheKey(focus, turn);
    if (audioCacheRef.current.has(key)) {
      return audioCacheRef.current.get(key)!;
    }

    const existing = prefetchInFlightRef.current.get(key);
    if (existing) return existing;

    const request = voiceApi.preview(
      STRESS_TEST_VOICE_ID,
      content,
      STRESS_TEST_TTS_MODEL,
      'English (Global)',
    )
      .then((blob) => {
        audioCacheRef.current.set(key, blob);
        prefetchInFlightRef.current.delete(key);
        return blob;
      })
      .catch((error) => {
        console.warn('[StressTest prefetch]', key, error);
        prefetchInFlightRef.current.delete(key);
        return null;
      });

    prefetchInFlightRef.current.set(key, request);
    return request;
  }, []);

  const resolveAudioForScript = useCallback(async (
    focus: StressTestFocusId | null | undefined,
    turn: number,
    script: string,
  ) => {
    const key = focus ? stressTestScriptCacheKey(focus, turn) : null;
    if (key) {
      const cached = audioCacheRef.current.get(key);
      if (cached) {
        return prepareSpeechAudioFromBlob(cached);
      }

      const inFlight = prefetchInFlightRef.current.get(key);
      if (inFlight) {
        const blob = await inFlight;
        if (blob) return prepareSpeechAudioFromBlob(blob);
      }
    }

    return prepareSpeechAudio(
      STRESS_TEST_VOICE_ID,
      script,
      'English (Global)',
      STRESS_TEST_TTS_MODEL,
    );
  }, []);

  const stopSyncAnimation = useCallback(() => {
    if (syncRafRef.current != null) {
      cancelAnimationFrame(syncRafRef.current);
      syncRafRef.current = null;
    }
  }, []);

  const stopMicListening = useCallback(() => {
    micActiveRef.current = false;
    const recognition = recognitionRef.current as {
      stop: () => void;
      onresult: unknown;
      onend: unknown;
      onerror: unknown;
    } | null;
    recognitionRef.current = null;
    if (recognition) {
      try {
        recognition.onresult = null;
        recognition.onend = null;
        recognition.onerror = null;
        recognition.stop();
      } catch {
        /* ignore */
      }
    }
    setListening(false);
  }, []);

  const finishSpeech = useCallback((script: string) => {
    stopSyncAnimation();
    setTypedContent(script);
    setSpeakingIndex(null);
    setLoading(false);
    setInput('');
  }, [stopSyncAnimation]);

  const runAudioSyncedTypewriter = useCallback((script: string, audio: HTMLAudioElement) => {
    const durationMs = audio.duration && Number.isFinite(audio.duration) && audio.duration > 0
      ? audio.duration * 1000
      : estimateSpeechDurationMs(script);

    syncDurationRef.current = durationMs;
    syncStartRef.current = performance.now();

    const tick = () => {
      const elapsed = performance.now() - syncStartRef.current;
      const progress = Math.min(1, elapsed / syncDurationRef.current);
      const charCount = Math.max(1, Math.ceil(progress * script.length));
      setTypedContent(script.slice(0, charCount));

      if (progress < 1 && !audio.ended) {
        syncRafRef.current = requestAnimationFrame(tick);
        return;
      }

      finishSpeech(script);
    };

    syncRafRef.current = requestAnimationFrame(tick);
  }, [finishSpeech]);

  const playSyncedAssistantMessage = useCallback(async (
    script: string,
    messageIndex: number,
    focus: StressTestFocusId | null | undefined,
    turn: number,
  ) => {
    stopMicListening();
    stopSyncAnimation();
    cancelSpeechPlayback();
    resetAudioPlaybackGate();
    setSpeakingIndex(messageIndex);
    setTypedContent(script.charAt(0) || '');
    setInput('');

    const runTypewriterOnly = () => {
      const durationMs = estimateSpeechDurationMs(script);
      syncDurationRef.current = durationMs;
      syncStartRef.current = performance.now();

      const tick = () => {
        const elapsed = performance.now() - syncStartRef.current;
        const progress = Math.min(1, elapsed / syncDurationRef.current);
        const charCount = Math.max(1, Math.ceil(progress * script.length));
        setTypedContent(script.slice(0, charCount));

        if (progress < 1) {
          syncRafRef.current = requestAnimationFrame(tick);
          return;
        }

        finishSpeech(script);
      };

      syncRafRef.current = requestAnimationFrame(tick);
    };

    try {
      const audio = await resolveAudioForScript(focus, turn, script);
      speechRef.current = audio;

      audio.onended = () => finishSpeech(script);

      await audio.play();
      runAudioSyncedTypewriter(script, audio);
    } catch (error) {
      console.warn('[StressTest voice fallback]', error);
      runTypewriterOnly();
    }
  }, [finishSpeech, resolveAudioForScript, runAudioSyncedTypewriter, stopMicListening, stopSyncAnimation]);

  useEffect(() => {
    getOrCreateGuestSessionId();
    document.body.style.overflow = 'hidden';

    let cancelled = false;
    const scripts = getAllStressTestScripts();

    (async () => {
      // Warm fixed Turn 1 scripts only. Later turns reflect live user meaning.
      await Promise.all(scripts.map((s) => prefetchScript(s.focusArea, s.turnNumber, s.content)));
      if (!cancelled) setVoiceReady(true);
    })();

    return () => {
      cancelled = true;
      document.body.style.overflow = '';
      stopSyncAnimation();
      if (speechRef.current) {
        speechRef.current.pause();
        speechRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          /* ignore */
        }
        recognitionRef.current = null;
      }
      cancelSpeechPlayback();
      audioCacheRef.current.clear();
      prefetchInFlightRef.current.clear();
    };
  }, [prefetchScript, stopSyncAnimation]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages, loading, isLocked, typedContent, speakingIndex]);

  const isSpeaking = speakingIndex !== null;

  const submitUserMessage = useCallback(async (userMsg: string, focus?: StressTestFocusId) => {
    if (!userMsg.trim() || loading || isLocked || isSpeaking) return;

    // Stop mic before TTS so André's reply is never transcribed into the input box.
    stopMicListening();
    setInput('');

    const resolvedFocus = focus ?? focusArea;
    const priorMessages = [...messages, { role: 'user' as const, content: userMsg.trim() }];
    setMessages(priorMessages);
    setLoading(true);

    try {
      const turnNumber = countUserTurns(priorMessages);
      const priorUserTurns = priorMessages.filter((message) => message.role === 'user');
      const priorUserContent =
        turnNumber >= 3 && priorUserTurns.length >= 2
          ? priorUserTurns[priorUserTurns.length - 2]?.content
          : null;
      const result = buildGuestStressTestResponse({
        turnNumber,
        content: userMsg.trim(),
        focusArea: resolvedFocus,
        priorUserContent,
      });

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: result.content,
        focusArea: result.focusArea,
      };

      setMessages((current) => [...current, assistantMsg]);
      setIsLocked(result.isLocked);

      // Keep the composer empty while the mentor speaks.
      setInput('');

      await playSyncedAssistantMessage(
        result.content,
        priorMessages.length,
        result.focusArea,
        result.turnNumber,
      );

      setInput('');
    } catch {
      setMessages((current) => [
        ...current,
        { role: 'system', content: 'Unable to process your response. Please try again.' },
      ]);
      setLoading(false);
      setInput('');
    }
  }, [focusArea, isLocked, isSpeaking, loading, messages, playSyncedAssistantMessage, stopMicListening]);

  const handleFocusSelect = (focus: StressTestFocusId) => {
    if (isLocked || loading || isSpeaking || focusArea) return;
    // Unlock autoplay within the user-gesture chain (needed for iframe previews).
    try {
      const unlock = new Audio();
      unlock.muted = true;
      void unlock.play().then(() => {
        unlock.pause();
      }).catch(() => {});
    } catch {
      /* ignore */
    }
    setFocusArea(focus);
    const label = STRESS_TEST_FOCUS_AREAS.find((f) => f.id === focus)?.label ?? focus;
    void submitUserMessage(`Focus area: ${label}`, focus);
  };

  const handleUnlock = () => {
    const transcript: GuestTranscriptMessage[] = messages.filter(
      (m) => m.role === 'user' || m.role === 'assistant',
    );
    savePendingGuestTranscript(transcript);
    navigate('/register-private');
  };

  const toggleMic = () => {
    const win = window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionInstance;
      webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    };
    type SpeechRecognitionInstance = {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: {
        resultIndex: number;
        results: ArrayLike<{ 0: { transcript: string }; isFinal?: boolean } & { length: number }>;
      }) => void) | null;
      onend: (() => void) | null;
      onerror: ((event: { error?: string }) => void) | null;
    };

    const SpeechRecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listening || micActiveRef.current) {
      stopMicListening();
      return;
    }

    if (isSpeaking || loading || isLocked || !focusArea) return;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';

    let finalized = '';
    micActiveRef.current = true;

    recognition.onresult = (event) => {
      if (!micActiveRef.current) return;
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const piece = result[0]?.transcript || '';
        if (result.isFinal) {
          finalized = `${finalized} ${piece}`.replace(/\s+/g, ' ').trim();
        } else {
          interim += piece;
        }
      }
      const next = `${finalized} ${interim}`.replace(/\s+/g, ' ').trim();
      setInput(next);
    };

    recognition.onend = () => {
      // Chrome often ends continuous sessions after a pause — keep listening until user/send stops it.
      if (micActiveRef.current && recognitionRef.current === recognition) {
        try {
          recognition.start();
          return;
        } catch {
          stopMicListening();
        }
        return;
      }
      setListening(false);
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted' || event.error === 'no-speech') {
        return;
      }
      stopMicListening();
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const showFocusSelector = !isLocked;
  const inputDisabled = loading || isLocked || !focusArea || isSpeaking;

  return (
    <div className="stress-test-screen" data-screen-id={SCREENS.PRO08X}>
      <header className="stress-test-header">
        <div className="stress-test-logo-wrap">
          <img
            src={LOGO}
            alt="Mentology"
            className="stress-test-logo brand-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <h1 className="stress-test-subtitle">10-Minute Business Strategy Stress-Test</h1>
      </header>

      <div className="stress-test-body">
        <div
          ref={streamRef}
          className={`stress-test-stream ${SPOKEN_TEXT_ATTRS.className}`}
        >
          <div className={`stress-test-welcome scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
            {WELCOME_SCRIPT}
          </div>

          {showFocusSelector && (
            <div className="stress-test-focus-grid" role="group" aria-label="Strategic focus selection">
              {STRESS_TEST_FOCUS_AREAS.map((area) => {
                const selected = focusArea === area.id;
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={`stress-test-focus-btn${selected ? ' stress-test-focus-btn-selected' : ''}`}
                    onClick={() => handleFocusSelect(area.id)}
                    disabled={loading || isSpeaking || Boolean(focusArea)}
                    aria-pressed={selected}
                  >
                    {area.label}
                  </button>
                );
              })}
            </div>
          )}

          {!focusArea && !voiceReady && (
            <p className="stress-test-voice-prep">Preparing AI Mentor voice…</p>
          )}

          <div className="stress-test-thread">
            {messages.map((message, index) => {
              const isActiveAssistant = message.role === 'assistant' && index === speakingIndex;
              const visibleText = isActiveAssistant ? typedContent : message.content;

              return (
                <div
                  key={index}
                  className={`stress-test-bubble stress-test-bubble-${message.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
                >
                  {visibleText}
                  {isActiveAssistant && typedContent.length < message.content.length && (
                    <span className="stress-test-cursor">|</span>
                  )}
                </div>
              );
            })}
            {loading && !isSpeaking && (
              <p className={`stress-test-bubble scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
                Processing...
              </p>
            )}
          </div>

          {isLocked && !isSpeaking && (
            <div className="stress-test-intercept-card" role="region" aria-label="Conversion intercept">
              <h2 className="stress-test-intercept-title">Strategic Vulnerabilities Identified</h2>
              <p className="stress-test-intercept-body">
                Your session logic has been audited and 2 critical risk factors were logged.
                To unlock your complete, risk-mitigated Action Learning Plan and save this
                transcript to your private vault, initialize your workspace.
              </p>
              <button
                type="button"
                className="stress-test-intercept-cta"
                onClick={handleUnlock}
              >
                Unlock Full Action Plan &amp; Start 14-Day Free Trial
              </button>
            </div>
          )}
        </div>
      </div>

      <form
        className="stress-test-input-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          if (input.trim() && !inputDisabled) {
            void submitUserMessage(input);
          }
        }}
      >
        <textarea
          className="stress-test-chat-input"
          placeholder={
            isSpeaking
              ? 'AI Mentor is speaking...'
              : isLocked
                ? 'Session complete — initialize your workspace to continue.'
                : !focusArea
                  ? 'Select a strategic focus area above to begin...'
                  : 'Share your execution logic, metrics, or response...'
          }
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (input.trim() && !inputDisabled) {
                void submitUserMessage(input);
              }
            }
          }}
          disabled={inputDisabled}
          aria-label="Guest message input"
        />
        <div className="stress-test-input-controls">
          <button
            type="submit"
            className="stress-test-send-btn"
            disabled={inputDisabled || !input.trim()}
          >
            Send
          </button>
          <button
            type="button"
            className={`stress-test-mic-btn ${listening ? 'stress-test-mic-active' : ''}`}
            onClick={toggleMic}
            disabled={inputDisabled}
            aria-label="Microphone"
            title="Microphone"
          >
            🎙️
          </button>
        </div>
      </form>
    </div>
  );
}
